import { Layout } from "@/components/Layout";

export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-4xl font-bold mb-2 text-white">Privacy Policy</h1>
        <p className="text-gray-300 mb-8">Last updated: November 2025</p>

        <div className="space-y-8 text-sm leading-relaxed text-white">
          <div>
            <p className="mb-4">
              ShiftBear ("we," "our," or "us") operates the ShiftBear: Clean Shift Planner mobile application (the "App").
            </p>
            <p>
              This Privacy Policy explains how we collect, use, and protect your personal information when you use our App.
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">1. Information We Collect</h2>
            <p className="mb-4">We collect only the data necessary to provide and improve the App's functionality.</p>
            
            <div className="ml-4 space-y-4">
              <div>
                <h3 className="font-semibold mb-2 text-white">a. Information You Provide</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li>Account information: Email address and password (for login and sync).</li>
                  <li>Shift data: Work schedules, shift times, and notes you manually create.</li>
                  <li>Team sharing data: Optional sharing of schedule details with other users.</li>
                  <li>Support requests: Information you provide when contacting us for help.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2 text-white">b. Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li>Usage data: App usage events (e.g., feature usage, errors, session length).</li>
                  <li>Device data: Device type, OS version, and performance metrics (non-identifiable).</li>
                </ul>
                <p className="mt-2 text-gray-300">We do not collect location data, contact lists, or sensitive personal information.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">2. How We Use Information</h2>
            <p className="mb-2">We use collected data to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>Provide core functionality (calendar, reminders, sync).</li>
              <li>Maintain backups and real-time sync via Supabase.</li>
              <li>Improve performance and fix bugs.</li>
              <li>Communicate with you about updates or support (if you contact us).</li>
            </ul>
            <p className="mt-4 font-semibold">We never sell or rent your personal information.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">3. Data Storage and Security</h2>
            <p className="mb-2">All user data is securely stored using Supabase (PostgreSQL + Authentication) with:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>Encryption in transit (HTTPS/TLS)</li>
              <li>Access control limited to authenticated users</li>
              <li>Secure cloud storage in compliant data centers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">4. Data Sharing</h2>
            <p className="mb-2">We do not share user data with third parties, except:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>Service providers (Supabase) who process data on our behalf for hosting and authentication.</li>
              <li>When required by law, to comply with legal obligations.</li>
            </ul>
            <p className="mt-2 text-gray-300">These providers are bound by confidentiality and data protection agreements.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">5. Data Retention and Deletion</h2>
            <p className="mb-2">You can delete your account and associated data at any time by:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>Visiting: <a href="/delete-account" className="text-primary hover:underline">https://shiftbear.com/delete-account</a></li>
              <li>Contacting us at <a href="mailto:support@shiftbear.com" className="text-primary hover:underline">support@shiftbear.com</a></li>
            </ul>
            <p className="mt-4 text-gray-300">When you delete your account:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>All user-specific data (account info, shifts, teams) are permanently removed.</li>
              <li>Backup copies may persist for up to 30 days before automatic deletion.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">6. Children's Privacy</h2>
            <p className="mb-2">ShiftBear is not intended for children under 13.</p>
            <p className="text-gray-300">We do not knowingly collect personal data from children. If you believe a child has provided personal data, please contact us to delete it.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">7. Your Rights</h2>
            <p className="mb-2">Depending on your region, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
              <li>Access, update, or delete your data.</li>
              <li>Withdraw consent or restrict processing.</li>
              <li>File a complaint with a data protection authority.</li>
            </ul>
            <p className="mt-4 text-gray-300">To exercise these rights, contact us at <a href="mailto:support@shiftbear.com" className="text-primary hover:underline">support@shiftbear.com</a>.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">8. Changes to This Policy</h2>
            <p className="text-gray-300">We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">9. Contact Us</h2>
            <p className="text-gray-300">If you have questions about this Privacy Policy, contact us at:</p>
            <div className="mt-3 space-y-1 text-gray-300">
              <p>📧 <a href="mailto:support@shiftbear.com" className="text-primary hover:underline">support@shiftbear.com</a></p>
              <p>🌐 <a href="https://shiftbear.com/privacy" className="text-primary hover:underline">https://shiftbear.com/privacy</a></p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
